<p-toast key="credit-history" position="top-right"></p-toast>
<app-file-preview-dialog
  [visible]="!!preview"
  [fileBlob]="preview?.blob ?? null"
  [fileName]="preview?.fileName ?? null"
  [downloadable]="true"
  (visibleChange)="$event || closePreview()"
></app-file-preview-dialog>

<div class="credit-history">
  <div class="empty-state" *ngIf="loading && !view" data-test="credit-loading">
    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
    <span>Cargando el historial crediticio…</span>
  </div>

  <div class="empty-state is-error" *ngIf="loadError" role="alert" data-test="credit-error">
    <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
    <h3>No pudimos cargar esta sección</h3>
    <span>{{ loadError }}</span>
    <button pButton type="button" class="p-button-outlined p-button-sm" icon="pi pi-refresh" label="Reintentar" (click)="load()"></button>
  </div>

  <ng-container *ngIf="view && !loadError">
    <!-- Autorización: la vigente, o la última con su estado. -->
    <div class="info-card" data-test="credit-current">
      <div class="card-header">
        <span class="card-icon is-blue"><i class="fa-solid fa-file-signature" aria-hidden="true"></i></span>
        <div class="header-title-group">
          <span class="card-eyebrow">Buró y Círculo</span>
          <h3>Autorización para consultar el historial crediticio</h3>
        </div>
        <div class="header-tags" *ngIf="latest as item">
          <p-tag
            [severity]="statusOf(item).severity"
            [icon]="statusOf(item).icon"
            [value]="statusOf(item).label"
            data-test="credit-status"
          ></p-tag>
        </div>
      </div>

      <div class="card-content">
        <ng-container *ngIf="latest as item; else noAuthorization">
          <!-- Hay una vigente y además uno nuevo esperando: la tarjeta muestra la vigente, pero el nuevo no puede quedar escondido. -->
          <p class="note" *ngIf="pending && pending.id !== item.id" data-test="credit-replacement-note">
            <i class="pi pi-clock" aria-hidden="true"></i>
            Hay un formato nuevo esperando la validación de Legal desde el {{ pending.createdAt | mxDate }}. Mientras tanto sigue vigente el
            aprobado.
          </p>
          <p class="note" *ngIf="stateOf(item) === 'pending'" data-test="credit-pending-note">
            <i class="pi pi-clock" aria-hidden="true"></i>
            Esperando la validación de Legal desde el {{ item.createdAt | mxDate }}. Sin un formato aprobado no se puede consultar el
            historial.
          </p>
          <p class="note is-bad" *ngIf="item.status === 'rejected'" data-test="credit-rejected-note">
            <i class="pi pi-times-circle" aria-hidden="true"></i>
            Legal rechazó el formato: «{{ item.rejectionReason }}». Sube uno corregido.
          </p>
          <p class="note" *ngIf="stateOf(item) === 'expired'" data-test="credit-expired-note">
            <i class="pi pi-calendar-times" aria-hidden="true"></i>
            El formato venció el {{ item.validUntil | slice: 0 : 10 | mxDate }}. Sube uno firmado de nuevo.
          </p>

          <div class="compact-grid">
            <div class="compact-item">
              <span class="label">Archivo</span>
              <span class="value">{{ item.documentName || 'Formato' }}</span>
            </div>
            <div class="compact-item">
              <span class="label">Mandado por</span>
              <span class="value">{{ item.uploadedBy.name }} · {{ item.createdAt | mxDate }}</span>
            </div>
            <ng-container *ngIf="item.status === 'approved'">
              <div class="compact-item">
                <span class="label">Firmado el</span>
                <span class="value">{{ item.signedAt | slice: 0 : 10 | mxDate }}</span>
              </div>
              <div class="compact-item">
                <span class="label">Vigente hasta</span>
                <span class="value" [class.success]="stateOf(item) === 'approved'">{{ item.validUntil | slice: 0 : 10 | mxDate }}</span>
              </div>
              <div class="compact-item" *ngIf="item.personType">
                <span class="label">Tipo de persona</span>
                <span class="value">{{ personTypeLabels[item.personType] }}</span>
              </div>
              <div class="compact-item" *ngIf="item.subject?.legalRepresentative">
                <span class="label">Representante legal</span>
                <span class="value">{{ item.subject?.legalRepresentative }}</span>
              </div>
            </ng-container>
            <div class="compact-item" *ngIf="item.reviewedBy">
              <span class="label">Revisó</span>
              <span class="value">{{ item.reviewedBy.name }} · {{ item.reviewedAt | mxDate }}</span>
            </div>
          </div>

          <div class="actions">
            <button
              pButton
              type="button"
              class="p-button-outlined p-button-sm"
              icon="pi pi-eye"
              label="Ver formato"
              [disabled]="busy"
              (click)="openDocument(item)"
              data-test="credit-open"
            ></button>
            <a
              *ngIf="canValidate && item.status === 'pending'"
              pButton
              class="p-button-sm"
              icon="pi pi-check-square"
              label="Validar"
              [routerLink]="['/credit-history/validations', item.id]"
              data-test="credit-validate"
            ></a>
          </div>
        </ng-container>

        <ng-template #noAuthorization>
          <div class="empty-state" data-test="credit-empty">
            <i class="fa-solid fa-file-signature" aria-hidden="true"></i>
            <h3>Sin autorización</h3>
            <span>El cliente no tiene un formato de autorización validado. Sin él no se puede consultar su historial crediticio.</span>
          </div>
        </ng-template>

        <!-- Mandar uno nuevo reemplaza al pendiente: quien lo pidió al cliente lo puede corregir antes de que Legal lo revise. -->
        <label class="dropzone" [class.is-busy]="busy" data-test="credit-upload">
          <i class="pi pi-upload" aria-hidden="true"></i>
          <span>{{
            busy
              ? 'Subiendo…'
              : pending
                ? 'Reemplazar el formato pendiente con otro PDF firmado'
                : 'Subir el formato firmado (PDF) y mandarlo a validación'
          }}</span>
          <input type="file" accept="application/pdf" hidden [disabled]="busy" (change)="onFilePicked($event)" />
        </label>
      </div>
    </div>

    <!-- Consulta a Círculo (E5b): el botón de Legal, el reporte de la última encontrada y el historial de consultas. -->
    <div class="info-card" *ngIf="queryButton.state !== 'hidden' || view.queries.length" data-test="credit-circulo">
      <div class="card-header">
        <span class="card-icon is-green"><i class="fa-solid fa-magnifying-glass-chart" aria-hidden="true"></i></span>
        <div class="header-title-group">
          <span class="card-eyebrow">Círculo de Crédito</span>
          <h3>Reporte de crédito</h3>
        </div>
        <div class="header-tags" *ngIf="view.circulo.mode === 'sandbox'">
          <p-tag severity="warning" icon="pi pi-flask" value="Modo de pruebas" data-test="credit-sandbox"></p-tag>
        </div>
      </div>

      <div class="card-content">
        <p class="note" *ngIf="view.circulo.mode === 'sandbox' && view.circulo.enabled">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          Círculo está en modo de pruebas: responde con datos de ejemplo, no con el historial real del cliente.
        </p>
        <ng-container [ngSwitch]="queryButton.state">
          <p class="note" *ngSwitchCase="'in_progress'" data-test="credit-query-in-progress">
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
            Hay una consulta en curso. Recarga en unos segundos para ver el resultado.
          </p>
          <div class="note" *ngSwitchCase="'disabled'" data-test="credit-query-blocked">
            <i class="pi pi-lock" aria-hidden="true"></i>
            <div>
              Todavía no se puede consultar:
              <ul>
                <li *ngFor="let reason of queryButton.reasons">{{ reason }}</li>
              </ul>
            </div>
          </div>
          <p class="note is-bad" *ngSwitchCase="'with_warning'" data-test="credit-query-timeout">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            La última consulta no recibió respuesta de Círculo: no se sabe si se registró ni si se cobró.
          </p>
        </ng-container>

        <div class="actions" *ngIf="queryButton.state !== 'hidden'">
          <button
            pButton
            type="button"
            class="p-button-sm"
            [icon]="
              queryButton.state === 'in_progress'
                ? 'pi pi-spin pi-spinner'
                : queryButton.state === 'reuse'
                  ? 'pi pi-replay'
                  : 'pi pi-search'
            "
            [label]="
              queryButton.state === 'in_progress'
                ? 'Consultando…'
                : queryButton.state === 'reuse'
                  ? 'Usar la consulta reciente'
                  : queryButton.state === 'with_warning'
                    ? 'Consultar de nuevo'
                    : 'Consultar historial'
            "
            [disabled]="busy || queryButton.state === 'disabled' || queryButton.state === 'in_progress'"
            (click)="openQuery()"
            data-test="credit-query"
          ></button>
        </div>

        <!-- El reporte de la última consulta con historial: lo mismo que usa la calculadora de riesgo. -->
        <ng-container *ngIf="report as found; else noReport">
          <div class="report" *ngIf="found.summary as summary" data-test="credit-report">
            <p class="report-meta">
              Consulta del {{ found.fechaConsulta | mxDate
              }}<ng-container *ngIf="found.folioConsulta"> · folio {{ found.folioConsulta }}</ng-container>
              <ng-container *ngIf="found.status === 'reused'"> · reutilizada</ng-container>
            </p>
            <div class="mini-stats" *ngIf="reportTotals as totals">
              <div class="mini-stat">
                <span class="label">Cuentas abiertas</span>
                <span class="value" data-test="credit-report-open">{{ totals.openAccounts }}</span>
              </div>
              <div class="mini-stat">
                <span class="label">Saldo abierto</span>
                <span class="value" data-test="credit-report-balance">{{ totals.balance | currency }}</span>
              </div>
              <div class="mini-stat">
                <span class="label">Vencido</span>
                <span class="value" [class.is-bad]="totals.pastDue > 0" data-test="credit-report-past-due">{{
                  totals.pastDue | currency
                }}</span>
              </div>
              <div class="mini-stat">
                <span class="label">Peor atraso</span>
                <span class="value" [class.is-bad]="totals.worstDelayDays > 0">{{ totals.worstDelayDays }} días</span>
              </div>
              <div class="mini-stat">
                <span class="label">Consultas (3 meses)</span>
                <span class="value">{{ summary.inquiries3m ?? '—' }}</span>
              </div>
            </div>

            <p class="note" *ngIf="summary.sicIncomplete" data-test="credit-report-sic">
              <i class="pi pi-info-circle" aria-hidden="true"></i>
              Círculo indica que el reporte de otras sociedades de información está incompleto.
            </p>
            <p class="note is-bad" *ngIf="summary.preventionKeys.length || summary.severeEvents.length" data-test="credit-report-severe">
              <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
              <ng-container *ngIf="summary.preventionKeys.length"
                >Claves de prevención: {{ preventionSummary(summary.preventionKeys) }}.
              </ng-container>
              <ng-container *ngIf="summary.severeEvents.length"
                >Eventos graves (quita, quebranto o clave grave) en {{ summary.severeEvents.length }} cuenta(s).</ng-container
              >
            </p>
            <p class="note" *ngFor="let item of summary.unconverted" data-test="credit-report-unconverted">
              <i class="pi pi-info-circle" aria-hidden="true"></i>
              {{ item.accounts }} cuenta(s) en {{ item.currency }} quedaron fuera del cálculo: no hay tipo de cambio.
            </p>

            <div class="table-wrap" *ngIf="summary.financialAccounts.length">
              <table class="history-table" data-test="credit-report-financial">
                <caption class="p-hidden-accessible">
                  Cuentas financieras del reporte de Círculo
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Otorgante</th>
                    <th scope="col">Estado</th>
                    <th scope="col">Apertura</th>
                    <th scope="col" class="is-num">Saldo</th>
                    <th scope="col" class="is-num">Vencido</th>
                    <th scope="col" class="is-num">Peor atraso</th>
                    <th scope="col">Plazo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let account of summary.financialAccounts">
                    <td>{{ account.lender || '—' }}</td>
                    <td>{{ account.isOpen ? 'Abierta' : 'Cerrada' }}</td>
                    <td>{{ account.openedAt ? (account.openedAt | slice: 0 : 10 | mxDate) : '—' }}</td>
                    <td class="is-num">
                      {{ account.balance | currency }}<span class="muted" *ngIf="account.fx"> ({{ account.fx.currency }})</span>
                    </td>
                    <td class="is-num" [class.is-bad]="account.pastDue > 0">{{ account.pastDue | currency }}</td>
                    <td class="is-num">{{ account.worstDelayDays }} días</td>
                    <td>{{ account.revolving ? 'Revolvente' : account.termMonths ? account.termMonths + ' meses' : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="table-wrap" *ngIf="summary.commercialAccounts.length">
              <table class="history-table" data-test="credit-report-commercial">
                <caption class="p-hidden-accessible">
                  Cuentas comerciales (proveedores) del reporte de Círculo
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Proveedor</th>
                    <th scope="col" class="is-num">Saldo</th>
                    <th scope="col" class="is-num">Vencido</th>
                    <th scope="col" class="is-num">Peor atraso</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let account of summary.commercialAccounts">
                    <td>{{ account.lender || '—' }}</td>
                    <td class="is-num">{{ account.balance | currency }}</td>
                    <td class="is-num" [class.is-bad]="account.pastDue > 0">{{ account.pastDue | currency }}</td>
                    <td class="is-num">{{ account.worstDelayDays }} días</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </ng-container>
        <ng-template #noReport>
          <p class="muted" data-test="credit-report-empty">Todavía no hay un reporte de Círculo con historial de este cliente.</p>
        </ng-template>

        <div class="table-wrap" *ngIf="view.queries.length">
          <table class="history-table" data-test="credit-queries">
            <caption class="p-hidden-accessible">
              Consultas a Círculo de Crédito: resultado, folio, quién consultó y costo
            </caption>
            <thead>
              <tr>
                <th scope="col">Consultada</th>
                <th scope="col">Resultado</th>
                <th scope="col">Fecha en Círculo</th>
                <th scope="col">Folio</th>
                <th scope="col">Consultó</th>
                <th scope="col" class="is-num">Costo</th>
                <th scope="col">Detalle</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let query of view.queries; trackBy: trackById" data-test="credit-query-row">
                <td>{{ query.requestedAt | mxDate }}</td>
                <td>
                  <p-tag
                    [severity]="queryResultUi(query).severity"
                    [icon]="queryResultUi(query).icon"
                    [value]="queryResultUi(query).label"
                  ></p-tag>
                </td>
                <td>{{ query.fechaConsulta ? (query.fechaConsulta | mxDate) : '—' }}</td>
                <td>{{ query.folioConsulta ?? '—' }}</td>
                <td>{{ query.requestedBy.name }}</td>
                <td class="is-num">{{ query.cost === null ? 'Por confirmar' : (query.cost | currency) }}</td>
                <td class="reason">{{ query.errorMessage ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <p-dialog
      [(visible)]="queryDialogVisible"
      [modal]="true"
      [closable]="!busy"
      [draggable]="false"
      header="Consultar el historial en Círculo"
      styleClass="credit-query-dialog"
      [style]="{ width: '520px' }"
    >
      <div class="dialog-body" data-test="credit-query-dialog">
        <ng-container *ngIf="queryButton.state === 'reuse' && view.circulo.reusable as reusable; else newQuery">
          <p>
            Hay una consulta con historial hecha el {{ reusable.requestedAt | mxDate }}, dentro de los {{ view.circulo.reuseDays }} días: se
            reutiliza sin llamar a Círculo y <strong>sin costo</strong>.
          </p>
        </ng-container>
        <ng-template #newQuery>
          <p>
            Se consultará el Reporte de Crédito Consolidado de <strong>{{ view.client.name }}</strong> ({{ view.client.rfc }}) con los datos
            que Legal validó en el formato.
          </p>
          <p data-test="credit-query-cost">
            Costo: <strong>{{ view.circulo.cost === null ? 'por confirmar con Círculo' : (view.circulo.cost | currency) }}</strong>
          </p>
        </ng-template>
        <p class="note" *ngIf="view.circulo.mode === 'sandbox'">
          <i class="pi pi-flask" aria-hidden="true"></i>
          Modo de pruebas: la respuesta es de ejemplo.
        </p>
        <label class="acknowledge" *ngIf="queryButton.state === 'with_warning'" data-test="credit-query-acknowledge">
          <input type="checkbox" [checked]="acknowledgeCharge" (change)="acknowledgeCharge = $any($event.target).checked" />
          <span>La consulta anterior pudo cobrarse. Acepto un posible segundo cargo.</span>
        </label>
        <div class="dialog-actions">
          <button
            pButton
            type="button"
            class="p-button-outlined p-button-sm"
            label="Cancelar"
            [disabled]="busy"
            (click)="queryDialogVisible = false"
          ></button>
          <button
            pButton
            type="button"
            class="p-button-sm"
            icon="pi pi-search"
            [label]="busy ? 'Consultando…' : queryButton.state === 'reuse' ? 'Reutilizar' : 'Consultar'"
            [disabled]="busy || (queryButton.state === 'with_warning' && !acknowledgeCharge)"
            (click)="confirmQuery()"
            data-test="credit-query-confirm"
          ></button>
        </div>
      </div>
    </p-dialog>

    <!-- Formatos que ya están en el expediente (Documentos › «Formato revisar buró»). -->
    <div class="info-card" data-test="credit-files">
      <div class="card-header">
        <span class="card-icon is-purple"><i class="fa-solid fa-folder-open" aria-hidden="true"></i></span>
        <div class="header-title-group">
          <span class="card-eyebrow">Expediente</span>
          <h3>Formatos en el expediente</h3>
        </div>
      </div>
      <div class="card-content">
        <p class="muted" *ngIf="!view.expedienteFiles.length">No hay formatos en «Formato revisar buró» del expediente.</p>
        <ul class="file-list" *ngIf="view.expedienteFiles.length">
          <li *ngFor="let file of view.expedienteFiles; trackBy: trackById" data-test="credit-file">
            <i class="pi pi-file-pdf" aria-hidden="true"></i>
            <span class="file-name">{{ file.name || 'Formato' }}</span>
            <span class="muted" *ngIf="file.uploadedAt">{{ file.uploadedAt | mxDate }}</span>
            <p-tag *ngIf="file.reviewStatus === 'pending'" severity="warning" icon="pi pi-clock" value="Por aprobar en Documentos"></p-tag>
            <p-tag
              *ngIf="file.reviewStatus === 'rejected'"
              severity="danger"
              icon="pi pi-times-circle"
              value="Rechazado en Documentos"
            ></p-tag>
            <p-tag *ngIf="file.sent" severity="info" icon="pi pi-send" value="Ya se mandó"></p-tag>
            <button
              *ngIf="canSend(file)"
              pButton
              type="button"
              class="p-button-text p-button-sm send-button"
              icon="pi pi-send"
              label="Mandar a validación"
              [disabled]="busy"
              (click)="send(file)"
              data-test="credit-send"
            ></button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Todas las autorizaciones del cliente, de la más nueva a la más vieja. -->
    <div class="info-card" *ngIf="view.authorizations.length" data-test="credit-history-list">
      <div class="card-header">
        <span class="card-icon is-orange"><i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i></span>
        <div class="header-title-group">
          <span class="card-eyebrow">Historial</span>
          <h3>Autorizaciones del cliente</h3>
        </div>
      </div>
      <div class="card-content">
        <div class="table-wrap">
          <table class="history-table">
            <caption class="p-hidden-accessible">
              Autorizaciones del cliente: estado, fechas y quién las revisó
            </caption>
            <thead>
              <tr>
                <th scope="col">Mandada</th>
                <th scope="col">Estado</th>
                <th scope="col">Firmada</th>
                <th scope="col">Vigente hasta</th>
                <th scope="col">Revisó</th>
                <th scope="col">Motivo</th>
                <th scope="col"><span class="p-hidden-accessible">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of view.authorizations; trackBy: trackById" data-test="credit-history-row">
                <td>{{ item.createdAt | mxDate }}</td>
                <td><p-tag [severity]="statusOf(item).severity" [icon]="statusOf(item).icon" [value]="statusOf(item).label"></p-tag></td>
                <td>{{ item.signedAt ? (item.signedAt | slice: 0 : 10 | mxDate) : '—' }}</td>
                <td>{{ item.validUntil ? (item.validUntil | slice: 0 : 10 | mxDate) : '—' }}</td>
                <td>{{ item.reviewedBy?.name ?? '—' }}</td>
                <td class="reason">{{ item.rejectionReason ?? '—' }}</td>
                <td class="row-actions">
                  <button
                    pButton
                    type="button"
                    class="p-button-text p-button-sm"
                    icon="pi pi-eye"
                    [attr.aria-label]="'Ver el formato mandado el ' + (item.createdAt | mxDate)"
                    [disabled]="busy"
                    (click)="openDocument(item)"
                  ></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </ng-container>
</div>
